import { createSubscriptionInvoice } from "@/redux/slices/subscriptionInvoice";
import useAuth from "../../hooks/useAuth";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useState, useEffect, SetStateAction } from "react";
interface PlanModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newPlanId: string) => Promise<void> | void;
  currentPlanId: string;
  onDeactivate: () => void;
  plans: {
    id: string;
    name: string;
    price: number;
    currency: string;
    period: string;
    entitlements: string;
  }[];
}

const PlanModal = ({
  open,
  onClose,
  onConfirm,
  onDeactivate,
  currentPlanId,
  plans,
}: PlanModalProps) => {
  const { currentUser, loadAssociationSubscription } = useAuth();
  const [selectedPlanId, setSelectedPlanId] = useState(currentPlanId);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<any>(null);
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false);
  const associationId = currentUser?.activeAssociation.id;

  useEffect(() => {
    if (selectedPlanId) {
      const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
      setSelectedPlanDetails(selectedPlan?.entitlements ?? null);
    } else {
      setSelectedPlanDetails(null);
    }
  }, [selectedPlanId, plans]);

  useEffect(() => {
    setSelectedPlanId(currentPlanId);
  }, [currentPlanId]);

  const handlePlanChange = (event: {
    target: { value: SetStateAction<string> };
  }) => {
    setSelectedPlanId(event.target.value);
  };

  const handleConfirm = async () => {
    onClose();
    await onConfirm(selectedPlanId);
    await loadAssociationSubscription(associationId);
  };

  const handleDeactivateClick = () => {
    setShowConfirmDeactivate(true);
  };

  const handleConfirmDeactivate = () => {
    onClose();
    onDeactivate();
    setShowConfirmDeactivate(false);
  };

  const handleCancelDeactivate = () => {
    setShowConfirmDeactivate(false);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        {!showConfirmDeactivate ? (
          <>
            <Typography variant="h6" mb={2}>
              Muokkaa tilausta
            </Typography>
            <Typography variant="body1" mb={3}>
              Valitse uusi paketti tälle tiekunnalle.
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="plan-select-label">Valitse paketti</InputLabel>
              <Select
                labelId="plan-select-label"
                id="plan-select"
                value={selectedPlanId}
                label="Valitse paketti"
                onChange={handlePlanChange}
              >
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id.toString()}>
                    {plan.name} - {plan.price / 100} {plan.currency}/
                    {plan.period}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedPlanDetails && (
              <Box mt={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Paketin tiedot:
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="div"
                >
                  <ul>
                    <li>
                      <strong>Tallennustila:</strong>{" "}
                      {selectedPlanDetails.storage_mb} MB
                    </li>
                    <li>
                      <strong>Jäsenraja:</strong>{" "}
                      {selectedPlanDetails.members_limit}
                    </li>
                    <li>
                      <strong>Laskujen lähetys:</strong>{" "}
                      {selectedPlanDetails.send_invoices ? "Kyllä" : "Ei"}
                    </li>
                    <li>
                      <strong>Kutsujen lähetys:</strong>{" "}
                      {selectedPlanDetails.send_meeting_invites
                        ? "Kyllä"
                        : "Ei"}
                    </li>
                    <li>
                      <strong>Postilähetys:</strong>{" "}
                      {selectedPlanDetails.postal_mail ? "Kyllä" : "Ei"}
                    </li>
                  </ul>
                </Typography>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" mt={3}>
              {/* Vasemman laidan Peruuta tilaus -painike */}
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleDeactivateClick}
              >
                Peruuta tilaus
              </Button>

              {/* Oikean laidan Peruuta ja Vahvista -painikkeet */}
              <Box display="flex" gap={2}>
                <Button variant="outlined" onClick={onClose}>
                  Peruuta
                </Button>
                <Button variant="contained" onClick={handleConfirm}>
                  Vahvista
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" mb={2}>
              Peruuta tilaus
            </Typography>
            <Typography variant="body1" mb={3}>
              Oletko varma, että haluat peruuttaa tilauksen? Tämän jälkeen
              tietoa ei voi enää muokata tai hakea.
            </Typography>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button variant="outlined" onClick={handleCancelDeactivate}>
                Peruuta
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDeactivate}
              >
                Vahvista
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default PlanModal;
